<template>
  <div class="m-chart-columns">
    <p
      v-if="!series.length"
      class="m-chart-columns-empty"
    >
      {{ emptyText }}
    </p>
    <div
      v-else
      class="m-chart-columns-body"
    >
      <div class="m-chart-columns-axis-y">
        <span
          v-for="tick in ticks"
          :key="'y-' + tick.key"
          class="m-chart-columns-axis-y-tick"
          :style="{ bottom: tick.bottom }"
        >
          {{ tick.text }}
        </span>
      </div>
      <div class="m-chart-columns-plot-wrapper">
        <div class="m-chart-columns-plot">
          <!-- Rejilla: hairlines solidas, un escalon por encima del fondo -->
          <span
            v-for="tick in ticks"
            :key="'grid-' + tick.key"
            class="m-chart-columns-grid"
            :style="{ bottom: tick.bottom }"
          ></span>
          <!-- Linea de referencia: va en la MISMA unidad que las columnas, nunca en otra escala -->
          <span
            v-if="thresholdBottom"
            class="m-chart-columns-threshold"
            :style="{ bottom: thresholdBottom }"
          >
            <span
              v-if="thresholdText"
              class="m-chart-columns-threshold-label"
            >
              {{ thresholdText }}
            </span>
          </span>
          <div
            v-for="(item, index) in series"
            :key="'col-' + item.key"
            :class="['m-chart-columns-slot', 'a-tooltip-primary', 'a-tooltip-top', tooltipCorner(index)]"
            :data-tooltip-text="item.tooltip"
          >
            <!-- La etiqueta directa va SOLO en la columna mas alta: un numero sobre cada barra no
                 se lee. El resto de los valores se leen en el eje y en el tooltip -->
            <span
              v-if="index === maxIndex && maxLabel"
              class="m-chart-columns-peak"
            >
              {{ maxLabel }}
            </span>
            <span
              class="m-chart-columns-bar"
              :style="{ backgroundColor: color, height: barHeight(item.value) }"
            ></span>
          </div>
        </div>
        <div class="m-chart-columns-axis-x">
          <span
            v-for="tick in ticksX"
            :key="'x-' + tick.key"
            class="m-chart-columns-axis-x-tick"
            :style="{ left: tick.left, transform: tick.transform }"
          >
            {{ tick.text }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 | Grafico de columnas hecho a mano con HTML y CSS: app-sales no tiene ninguna libreria de graficos
 | y este panel interno no justifica agregar una dependencia.
 |
 | Es de UNA sola serie (por eso no lleva leyenda: el titulo del bloque dice que se esta midiendo),
 | con una columna por punto en el orden en que llegan. Quien lo usa le pasa la serie ya armada y ya
 | rellenada con ceros: el grafico no inventa ni completa datos.
 */
export default {
  name: 'MoleculeChartColumns',

  props: {
    // Color de la serie, ya resuelto por quien lo usa
    color: {
      type: String,
      default: '#2a78d6'
    },

    // Texto cuando no hay ni un punto que dibujar
    emptyText: {
      type: String,
      default: ''
    },

    // Formatea los valores del eje y la etiqueta del pico
    formatter: {
      type: Function,
      default: value => `${value}`
    },

    // [{ key, label, value, tooltip }] — label es la marca del eje x, tooltip el texto al pasar
    series: {
      type: Array,
      default: () => []
    },

    // Linea de referencia opcional, SIEMPRE en la misma unidad que la serie (nunca un segundo eje)
    threshold: {
      type: Number,
      default: 0
    },

    thresholdText: {
      type: String,
      default: ''
    }
  },

  computed: {
    /*
     | La escala llega hasta el mayor entre la serie y la linea de referencia, asi la linea siempre
     | entra en el dibujo. Si todo es cero se usa 1 para no dividir por cero.
     */
    scale () {
      const tope = Math.max(this.maxValue, Math.max(Number(this.threshold) || 0, 0))

      return tope > 0 ? tope : 1
    },

    thresholdBottom () {
      const valor = Math.max(Number(this.threshold) || 0, 0)

      return valor > 0 ? `${(valor / this.scale) * 100}%` : ''
    },

    maxIndex () {
      return this.series.reduce(
        (mejor, item, index) => (item.value > this.series[mejor].value ? index : mejor),
        0
      )
    },

    maxLabel () {
      return this.maxValue > 0 ? this.formatter(this.maxValue) : ''
    },

    maxValue () {
      return this.series.reduce((mayor, item) => (item.value > mayor ? item.value : mayor), 0)
    },

    // Tres marcas: el maximo, la mitad y el cero
    ticks () {
      const tope = this.maxValue > 0 || this.threshold > 0 ? this.scale : 0

      return [
        { key: 'max', bottom: '100%', text: this.formatter(tope) },
        { key: 'mid', bottom: '50%', text: this.formatter(tope / 2) },
        { key: 'zero', bottom: '0%', text: this.formatter(0) }
      ]
    },

    /*
     | Marcas del eje x: solo la primera, la del medio y la ultima. Una etiqueta por columna se
     | superpone en cuanto hay mas de una decena de puntos.
     */
    ticksX () {
      const total = this.series.length

      if (!total) {
        return []
      }

      if (total === 1) {
        return [{ key: 'unico', left: '50%', text: this.series[0].label, transform: 'translateX(-50%)' }]
      }

      const medio = Math.floor((total - 1) / 2)
      const marcas = [
        { key: 'primero', indice: 0, left: '0%', transform: 'translateX(0)' },
        { key: 'medio', indice: medio, left: '50%', transform: 'translateX(-50%)' },
        { key: 'ultimo', indice: total - 1, left: '100%', transform: 'translateX(-100%)' }
      ]

      return marcas
        .filter((marca, index) => index === 0 || marca.indice !== marcas[index - 1].indice)
        .map(marca => ({
          key: marca.key,
          left: marca.left,
          text: this.series[marca.indice].label,
          transform: marca.transform
        }))
    }
  },

  methods: {
    barHeight (valor) {
      const alto = (Math.max(Number(valor) || 0, 0) / this.scale) * 100

      // Un valor mayor a cero nunca se dibuja invisible: se le deja un hilo de 2px
      return alto > 0 && alto < 1.5 ? '2px' : `${alto}%`
    },

    // El tooltip de las columnas de los bordes se corre hacia adentro para que no se salga del bloque
    tooltipCorner (index) {
      if (index <= 1) {
        return 'm-chart-columns-slot-start'
      }

      return index >= this.series.length - 2 ? 'm-chart-columns-slot-end' : ''
    }
  }
}
</script>
